import { base64ToFile } from '@/lib/utils'
import axios from 'axios'
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik'
import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'
import Select, { SingleValue } from 'react-select'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { ImageCropModal } from '@/components/image-crop-modal'
import { RootState } from 'features/store'
import statesncities from 'data/statesncities'
import { ProfileSettingsOpenProp } from 'types/common'
import {
    useUpdateListingImageMutation,
    useUpdateListingMutation,
    useUpdateProfileMutation,
    useUpdateUserAvatarMutation,
    useMyProfileQuery,
    useConfirmationCodeMutation,
    useConfirmEmailMutation,
    useReplaceListingOperationTimeMutation,
    useReplaceListingPracticesMutation,
    useGetPracticesQuery,
} from 'graphql/generated/graphql'
import { toast } from 'react-toastify'
import { useAppDispatch } from 'features/hooks'
import useFile from '@/hook/useFile'
import { logOut, logOutAndClearReminder, setUser } from 'features/authSlice'
import { useRouter } from 'next/navigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faCamera } from '@fortawesome/free-solid-svg-icons'
import { Button, Typography } from '@material-tailwind/react'
import CustomButton from '@/components/button'
import { Tooltip } from 'flowbite-react'
import Link from 'next/link'
import CatalogPicker from '@/components/business-onboarding/CatalogPicker'
import type { CatalogItem } from '@/lib/businessOnboarding'

type Option = {
    value: string;
    label: string;
};

// Accepts links typed/pasted without a scheme (e.g. "instagram.com/farm"), and repairs a
// scheme missing its slashes (e.g. mobile autofill producing "https:www.facebook.com/farm")
const normalizeUrl = (value?: string | null) => {
    let trimmed = (value || '').trim()
    if (!trimmed) return ''
    trimmed = trimmed.replace(/^(https?):(?!\/\/)/i, '$1://')
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const urlField = () =>
  z.preprocess((val) => normalizeUrl(val as string), z.string().url('Must be a valid URL').or(z.literal(''))).optional()

const BusinessInfoSchema = z.object({
  businessName: z
    .string()
    .min(2, 'Business name must be at least 2 characters').optional(),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address').or(z.literal('')).optional(),
  phoneNumber: z.string().optional().refine(val => !val || /^\d{10}$/.test(val), { message: 'Phone number must be 10 digits' }),
  description: z.string().min(5, 'Description is too short').optional(),
  tagLine: z.string().max(155, 'Tag line must be 155 characters or less').optional(),
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().regex(/^\d{5}$/, 'Invalid ZIP code').optional(),
  website: urlField(),
  facebook: urlField(),
  instagram: urlField(),
  twitter: urlField(),
})

const FIELD_LABELS: Record<string, string> = {
  businessName: 'Business name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phoneNumber: 'Phone number',
  description: 'Description',
  tagLine: 'Tag line',
  streetAddress: 'Street address',
  city: 'City',
  state: 'State',
  zip: 'ZIP code',
  website: 'Website',
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'X / Twitter',
}

const AccountInfo: React.FC<ProfileSettingsOpenProp> = ({ handleProfileSettingsOpen }) => {
    const router = useRouter()
    const dispatch = useAppDispatch()
    const [image, setImage] = useState<string | null>(null)
    const { file, setFile, url, setUrl } = useFile()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [showCropper, setShowCropper] = useState(false)
    const [showEmailTooltip, setShowEmailTooltip] = useState(false)
    const [newEmail, setNewEmail] = useState('')
    const [verificationCode, setVerificationCode] = useState('')
    const [codeSent, setCodeSent] = useState(false)
    const [emailVerified, setEmailVerified] = useState(false)
    const [sendingCode, setSendingCode] = useState(false)
    const [verifyingCode, setVerifyingCode] = useState(false)
    const [resendCooldown, setResendCooldown] = useState(0)
    const [locationOptedOut, setLocationOptedOut] = useState(false)
    const [certSearchTerm, setCertSearchTerm] = useState('')
    const [selectedPractices, setSelectedPractices] = useState<any[]>([])

    const [updateUserAvatar] = useUpdateUserAvatarMutation({ fetchPolicy: 'no-cache' })
    const [updateAvatar] = useUpdateListingImageMutation({ fetchPolicy: 'no-cache' })
    const [updateListing] = useUpdateListingMutation({ fetchPolicy: 'no-cache' })
    const [updateProfile, { loading: uploadLoader }] = useUpdateProfileMutation()
    const [sendConfirmationCode] = useConfirmationCodeMutation()
    const [confirmEmailCode] = useConfirmEmailMutation()
    const [replaceOperationTime] = useReplaceListingOperationTimeMutation({ fetchPolicy: 'no-cache' })
    const [replaceListingPracticesMutation] = useReplaceListingPracticesMutation({ fetchPolicy: 'no-cache' })
    const { data: practicesData, loading: practicesLoading } = useGetPracticesQuery()

    const certItems: CatalogItem[] = React.useMemo(
        () =>
            (practicesData?.getPractices || [])
                .filter((p: any) =>
                    !certSearchTerm.trim() || p.name?.toLowerCase().includes(certSearchTerm.toLowerCase())
                )
                .map((p: any) => ({ id: String(p.practice_id), name: p.name || 'Certificate', imageUrl: p.imgUrl || null })),
        [practicesData, certSearchTerm]
    )

    const selectedCertItems: CatalogItem[] = React.useMemo(
        () =>
            selectedPractices.map((p: any) => ({ id: String(p.practice_id), name: p.name || 'Certificate', imageUrl: p.imgUrl || null })),
        [selectedPractices]
    )

    const toggleCert = (item: CatalogItem) => {
        if (selectedPractices.some((p: any) => String(p.practice_id) === item.id)) {
            setSelectedPractices((prev: any[]) => prev.filter((p: any) => String(p.practice_id) !== item.id))
        } else {
            const full = practicesData?.getPractices?.find((p: any) => String(p.practice_id) === item.id)
            if (full) setSelectedPractices((prev: any[]) => [...prev, full])
        }
    }

    const { data, loading } = useMyProfileQuery({
        fetchPolicy: 'no-cache',
    })

    const isMarket = data?.myProfile?.user_type?.toLowerCase() === 'market'
    const isProducer = data?.myProfile?.user_type?.toLowerCase() === 'producer'
    const isBusiness = data?.myProfile?.user_type?.toLowerCase() !== 'general'

    const existingHours = data?.myProfile?.default_listing?.OperatingPeriod ?? []
    const initialOperatingHours = existingHours.length > 0
        ? existingHours.map((h: any) => ({
            id: h.operationId,
            day: h.day,
            time: h.openingTime && h.closingTime ? `${h.openingTime} - ${h.closingTime}` : h.openingTime || '',
          }))
        : [{ id: Date.now(), day: '', time: '' }]

    const initialValues = {
        businessName: data?.myProfile?.default_listing?.name || '',
        firstName: data?.myProfile?.default_listing?.firstName ||  data?.myProfile?.first_name || '',
        lastName: data?.myProfile?.default_listing?.lastName || data?.myProfile?.last_name || '',
        email: data?.myProfile?.default_listing?.email || data?.myProfile?.default_listing?.publicEmail || data?.myProfile?.email_address || '',

        phoneNumber: (() => {
            const raw = (data?.myProfile?.user_type.toLowerCase() === 'general' ? data?.myProfile?.phone_number : (data?.myProfile?.default_listing?.phone || data?.myProfile?.default_listing?.publicPhone)) || ''
            return /^[\d\s\-()+.]+$/.test(raw) ? raw.replace(/\D/g, '').slice(0, 10) : ''
        })(),

        description: (data?.myProfile?.user_type.toLowerCase() === 'general' ? data?.myProfile?.user_bio : data?.myProfile?.default_listing?.description) || '',
        tagLine: (data?.myProfile?.user_type?.toLowerCase() !== 'general' ? data?.myProfile?.default_listing?.tagLine : '') || '',
        streetAddress: data?.myProfile?.default_listing?.address || '',
        city: (data?.myProfile?.default_listing?.city ?? data?.myProfile?.city) || '',
        state: (data?.myProfile?.default_listing?.state ?? data?.myProfile?.state) || '',
        zip: (data?.myProfile?.default_listing?.zipcode ?? data?.myProfile?.zipcode) || '',
        image: (data?.myProfile?.user_type.toLowerCase() === 'general' ? data?.myProfile?.profile_photo_resource : data?.myProfile?.default_listing?.photo) || '',
        latitude: 0,
        longitude: 0,
        website: data?.myProfile?.default_listing?.website || '',
        facebook: data?.myProfile?.default_listing?.facebook || '',
        instagram: data?.myProfile?.default_listing?.instagram || '',
        twitter: data?.myProfile?.default_listing?.twitter || '',
        operatingHours: initialOperatingHours,
    }

    const handleSubmit = async (values: typeof initialValues, { setSubmitting }) => {
        const emailChanged = newEmail && newEmail.toLowerCase() !== data?.myProfile?.email_address?.toLowerCase()
        
        if (emailChanged && !emailVerified) {
            toast.error('Please verify your new email address before saving')
            setSubmitting(false)
            return
        }

        try {
            const emailToUpdate = emailChanged && emailVerified ? newEmail : values.email

            const profileInput: Record<string, any> = {
                email_address: emailToUpdate,
                first_name: values.firstName,
                last_name: values.lastName,
                profile_image_completed: !!(image || values.image || data?.myProfile?.profile_photo_resource),
                bio_completed: !!values.description,
                location_completed: !!(values.city || values.zip || values.latitude || values.longitude),
            }
            if (values.description !== initialValues.description) profileInput.user_bio = values.description
            if (values.phoneNumber !== initialValues.phoneNumber) profileInput.phone = values.phoneNumber
            if (values.streetAddress !== initialValues.streetAddress) profileInput.street_address = values.streetAddress
            if (values.city !== initialValues.city) profileInput.city = values.city
            if (values.state !== initialValues.state) profileInput.state = values.state
            if (values.zip !== initialValues.zip) profileInput.zipcode = values.zip
            if (values.latitude !== 0) profileInput.latitude = values.latitude
            if (values.longitude !== 0) profileInput.longitude = values.longitude

            const response = await updateProfile({
                variables: {
                    userID: data?.myProfile?.user_id,
                    input: profileInput,
                },
            })

            const formattedUserDetail = {
                email_address: response.data?.updateProfile.email_address,
                user_bio: response.data?.updateProfile.user_bio,
                first_name: response.data?.updateProfile.first_name,
                last_name: response.data?.updateProfile.last_name,
                profile_photo_resource: response.data?.updateProfile.profile_photo_resource || '',
                //user_id: response.data?.updateProfile.user_id,
                full_name: response.data?.updateProfile.full_name,
                slug: response.data?.updateProfile.slug,
                profile_image_completed: response.data?.updateProfile.profile_image_completed,
                bio_completed: response.data?.updateProfile.bio_completed,
                communication_completed: response.data?.updateProfile.communication_completed,
                location_completed: response.data?.updateProfile.location_completed,
            }

            if (file) {
                if(data?.myProfile?.user_type?.toLowerCase() === 'general') {
                    const resImg = (
                        await updateUserAvatar({
                            variables: {
                                userID: data?.myProfile?.user_id,
                                file
                            }
                        })
                    ).data

                    if(resImg?.updateUserAvatar) {
                        formattedUserDetail['profile_photo_resource'] = resImg?.updateUserAvatar?.img_path
                    }
                } else {
                    const resImg = (
                        await updateAvatar({
                            variables: {
                                listingID: data?.myProfile?.default_listing_id,
                                file,
                            },
                        })
                    ).data

                    if(resImg?.updateListingImage) {
                        formattedUserDetail['profile_photo_resource'] = resImg?.updateListingImage
                    }
                }
                
                dispatch(setUser(formattedUserDetail))
            }

            if (data?.myProfile?.user_type?.toLowerCase() !== 'general' && data?.myProfile?.default_listing_id) {
                const listingInput: Record<string, any> = {
                    tagLine: values.tagLine,
                    phone: values.phoneNumber,
                    website: normalizeUrl(values.website) || null,
                    facebook: normalizeUrl(values.facebook) || null,
                    instagram: normalizeUrl(values.instagram) || null,
                    twitter: normalizeUrl(values.twitter) || null,
                }
                if (values.firstName !== initialValues.firstName) listingInput.firstName = values.firstName
                if (values.lastName !== initialValues.lastName) listingInput.lastName = values.lastName
                if (values.description !== initialValues.description) listingInput.description = values.description
                if (values.city !== initialValues.city) listingInput.city = values.city
                if (values.state !== initialValues.state) listingInput.state = values.state
                if (values.zip !== initialValues.zip) listingInput.zipcode = values.zip
                if (values.streetAddress !== initialValues.streetAddress) listingInput.address = values.streetAddress
                if (values.latitude !== 0) listingInput.latitude = values.latitude
                if (values.longitude !== 0) listingInput.longitude = values.longitude

                await updateListing({
                    variables: {
                        listingId: data.myProfile.default_listing_id,
                        input: listingInput,
                    },
                })

                if (isProducer) {
                    await replaceListingPracticesMutation({
                        variables: {
                            listingId: data.myProfile.default_listing_id,
                            input: selectedPractices.map((p: any) => ({
                                practice_id: p.practice_id,
                                name: p.name || '',
                                resource_path: p.imgUrl || p.resource_path || '',
                            })),
                        },
                    })
                }

                if (isMarket && values.operatingHours.some((h: any) => h.day && h.time)) {
                    const hoursInput = values.operatingHours
                        .filter((h: any) => h.day && h.time)
                        .map((h: any) => {
                            const parts = h.time.split('-').map((s: string) => s.trim())
                            return {
                                listing_id: data.myProfile.default_listing_id,
                                day: h.day,
                                openingTime: parts[0] || h.time,
                                closingTime: parts[1] || '',
                            }
                        })
                    await replaceOperationTime({
                        variables: {
                            listingId: data.myProfile.default_listing_id,
                            input: hoursInput,
                        },
                    })
                }
            }

            if (response !== undefined) {
                if (emailChanged && emailVerified) {
                    toast.success("Email updated successfully! Please log in again with your new email.", {
                        autoClose: 3000
                    })
                    setSubmitting(false)
                    setTimeout(() => {
                        dispatch(logOutAndClearReminder())
                        router.push('/auth/login')
                    }, 3000)
                    return
                } else {
                    dispatch(setUser(formattedUserDetail))
                    
                    toast.success('Detail updated successfully!')
                }

                setSubmitting(false)
                setFile(null)
                handleProfileSettingsOpen()
                router.refresh()
            }
        } catch (error: any) {
            toast.error(error.message)
        }
    }

    const handleCroppedImage = useCallback(
        (croppedImageUrl: string, croppedFile: File, setFieldValue: Function) => {
          setFieldValue('image', croppedFile)
          setFile(croppedFile)
          setImage(croppedImageUrl)
          setShowCropper(false)
        },
        [setFile]
    )
    
    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target) {
                    setImage(event.target.result as string)
                    setShowCropper(true)
                }
            }
            reader.readAsDataURL(file)
        }},
    [])

    const handleStateChange = (selectedOption: SingleValue<Option>, setFieldValue: any) => {
        setFieldValue('state', selectedOption ? selectedOption.value : '');
        setFieldValue('city', '')
    };
    
    const handleStreetAddressBlur = async (streetAddress: string, city: string, state: string, setFieldValue: any) => {
        if (streetAddress && city && state) {
            const formattedAddress = `${streetAddress} ${city} ${state}`
            const response = await axios.get(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(formattedAddress)}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY}`
            )
            if (response.status === 200) {
                const latlng = response?.data?.results[0]?.geometry?.location
                if (latlng?.lat && latlng?.lng) {
                    setFieldValue('latitude', latlng.lat)
                    setFieldValue('longitude', latlng.lng)
                }
            }
        }
    }

    const handleCityChange = async (selectedOption: SingleValue<Option>, setFieldValue: any, state: string, address: string) => {
        setFieldValue('city', selectedOption ? selectedOption.value : '');
        if(selectedOption && selectedOption.value && state && address) {
            const formattedAddress = `${address} ${selectedOption.value} ${state}`
            const response = await axios.get(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                formattedAddress
                )}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY}`
            )

            if(response.status === 200){
                const latlng = response?.data?.results[0]?.geometry?.location
                const address = response.data.results[0]?.formatted_address
                const zipCodeMatch = address?.match(/\b\d{5}\b/);
                setFieldValue('zip', zipCodeMatch ? zipCodeMatch[0] : null)
                setFieldValue('latitude', latlng ? latlng?.lat : null)
                setFieldValue('longitude', latlng ? latlng?.lng : null)
            }
        }
    };

    const handleSendCode = async () => {
        if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
            toast.error('Please enter a valid email address')
            return
        }
        
        if (newEmail.toLowerCase() === data?.myProfile?.email_address?.toLowerCase()) {
            toast.error('This is your current email address')
            return
        }

        setSendingCode(true)
        try {
            const response = await sendConfirmationCode({
                variables: {
                    email_address: newEmail,
                    fName: data?.myProfile?.first_name || 'User'
                }
            })

            if (response.data?.sendEmailConfirmationCode?.success) {
                setCodeSent(true)
                setResendCooldown(60)
                toast.success(`Verification code sent to ${newEmail}`)
            } else {
                toast.error(response.data?.sendEmailConfirmationCode?.message || 'Failed to send code')
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to send verification code')
        } finally {
            setSendingCode(false)
        }
    };

    const handleVerifyCode = async () => {
        if (!verificationCode || verificationCode.trim().length !== 5) {
            toast.error('Please enter a valid 5-digit verification code')
            return
        }

        setVerifyingCode(true)
        try {
            const response = await confirmEmailCode({
                variables: {
                    email_address: newEmail,
                    code: verificationCode.trim()
                }
            })

            if (response.data?.confirmEmail) {
                setEmailVerified(true)
                setCodeSent(false)
                toast.success('Email verified successfully!')
            } else {
                toast.error('Invalid verification code')
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to verify code')
        } finally {
            setVerifyingCode(false)
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) return;
        await handleSendCode();
    };

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1)
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [resendCooldown])

    useEffect(() => {
        setLocationOptedOut(localStorage.getItem('culineer_location_opted_out') === 'true')
    }, [])

    useEffect(() => {
        if (data?.myProfile?.default_listing?.Practices) {
            setSelectedPractices(data.myProfile.default_listing.Practices.filter(Boolean))
        }
    }, [data])

    const handleLocationOptOut = async (checked: boolean) => {
        localStorage.setItem('culineer_location_opted_out', String(checked))
        setLocationOptedOut(checked)
        if (checked) {
            try {
                await updateProfile({
                    variables: {
                        userID: data?.myProfile?.user_id,
                        input: { location_completed: true },
                    },
                })
                dispatch(setUser({ location_completed: true }))
            } catch (e) {
                console.error('Failed to save location opt-out:', e)
            }
        }
    }

    if(loading) {
        return (
            <div className='flex justify-center items-center w-full'>
                <p>Please wait...</p>
            </div>
        )
    }

    return (
        <div>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={toFormikValidationSchema(BusinessInfoSchema)}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue, isSubmitting, ...formik }) => {
                    return (
                        <Form className="flex flex-col gap-4 px-2 pt-4">
                            <div className="relative h-full w-full">
                                <div className="flex justify-center">
                                    <div className="relative">
                                        {image || formik.values.image ? (
                                            <div className="h-40 w-40 overflow-hidden rounded-full">
                                                <img
                                                    src={image || formik.values.image || '/placeholder.svg'}
                                                    alt="Profile"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex h-40 w-40 items-center justify-center rounded-full bg-gray-200">
                                                <span className="text-gray-500 text-sm text-center px-2">No Image Selected</span>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="absolute bottom-2 right-2 h-10 w-10 flex items-center justify-center rounded-full bg-[#85C349] text-white shadow-lg hover:bg-[#75b339] transition-colors"
                                            style={{ transform: 'rotate(-45deg)' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faCamera}
                                                className="h-5 w-5"
                                                style={{ transform: 'rotate(45deg)' }}
                                            />
                                        </button>
                                    </div>
                                </div>

                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    onChange={handleFileChange}
                                    accept=".png,.jpg,.jpeg"
                                />

                                {showCropper && image && (
                                    <ImageCropModal
                                        image={image || '/placeholder.svg'}
                                        onCropComplete={(croppedImageUrl, croppedFile) => handleCroppedImage(croppedImageUrl, croppedFile, setFieldValue)}
                                        open={showCropper}
                                        onOpenChange={setShowCropper}
                                        aspect={1}
                                        fileName={
                                            data?.myProfile?.first_name && data?.myProfile?.last_name
                                                ? `${data.myProfile.first_name}-${data.myProfile.last_name}.png`.replace(/\s+/g, '-')
                                                : 'profile.png'
                                        }
                                        fileType="image/png"
                                        quality={0.9}
                                        maxSizeMB={2}
                                        maxWidthOrHeight={400}
                                        outputWidth={400}
                                        outputHeight={400}
                                        title="Crop Profile Image"
                                        showGrid={true}
                                        cropShape="round"
                                    />
                                )}
                            </div>
                            {/* {data?.myProfile?.user_type?.toLowerCase() !== 'general' && (
                                <div className="flex flex-col gap-2">
                                    <label>Business name</label>
                                    <Field
                                        type="text"
                                        name="businessName"
                                        placeholder="Enter name"
                                        className="h-14 w-full rounded-lg border border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                                    />
                                    <ErrorMessage
                                        name="businessName"
                                        component="div"
                                        className="text-sm text-red-500"
                                    />
                                </div>
                            )}   */}
                            <div className="flex flex-col gap-2">
                                <div className="flex gap-6">
                                    <div className="flex flex-1 flex-col">
                                        <label>First name</label>
                                        <Field
                                            type="text"
                                            name="firstName"
                                            placeholder="First Name"
                                            className="h-14 w-full rounded-lg border border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                                        />
                                        <ErrorMessage
                                            name="firstName"
                                            component="div"
                                            className="text-sm text-red-500"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col">
                                        <label>Last name</label>
                                        <Field
                                            type="text"
                                            name="lastName"
                                            placeholder="Last Name"
                                            className="h-14 w-full rounded-lg border border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                                        />
                                        <ErrorMessage
                                            name="lastName"
                                            component="div"
                                            className="text-sm text-red-500"
                                        />
                                    </div>
                                </div>
                            </div>
                            {data?.myProfile?.user_type?.toLowerCase() !== 'general' && (
                                <div className="flex flex-col gap-2">
                                    <label className="leading-[19.36px] text-[#202020]">
                                        Tag Line
                                    </label>
                                    <Field
                                        type="text"
                                        name="tagLine"
                                        placeholder="Brief summary shown on your listing card"
                                        maxLength={155}
                                        className="h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-base focus:border-primary focus:ring-primary"
                                    />
                                    <ErrorMessage name="tagLine" component="div" className="text-sm text-red-500" />
                                </div>
                            )}
                            <div className="flex flex-col gap-2">
                                <label className="leading-[19.36px] text-[#202020]">
                                    {isMarket ? 'About Your Market' : isProducer ? 'About Your Farm' : 'About You (bio)'}
                                </label>
                                <Field
                                    as="textarea"
                                    name="description"
                                    placeholder="Enthusiastic home cook ready to try new ingredients and recipes."
                                    rows={4}
                                    className="w-full resize-y rounded-lg border border-[#CBD7DD] px-4 py-2 placeholder:text-base focus:border-primary focus:ring-primary"
                                />
                                <ErrorMessage
                                    name="description"
                                    component="div"
                                    className="text-sm text-red-500"
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="leading-[19.36px] text-[#202020]">
                                    Change email address
                                </label>
                                <p className="text-sm text-gray-600 mb-1">
                                    Current email: <span className="font-medium">{data?.myProfile?.email_address}</span>
                                </p>
                                <div className="flex gap-2">
                                    <div className="flex-1 relative">
                                        <input
                                            type="email"
                                            value={newEmail || ''}
                                            onChange={(e) => {
                                                setNewEmail(e.target.value)
                                                setCodeSent(false)
                                                setEmailVerified(false)
                                                setVerificationCode('')
                                            }}
                                            placeholder="Enter new email address"
                                            disabled={emailVerified}
                                            className={`w-full h-14 rounded-lg border px-4 placeholder:text-base focus:border-primary focus:ring-primary ${
                                                emailVerified 
                                                    ? 'bg-green-50 border-green-500' 
                                                    : 'border-[#CBD7DD]'
                                            }`}
                                        />
                                        {emailVerified && (
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 font-medium text-sm">
                                                ✓ Verified
                                            </span>
                                        )}
                                    </div>
                                    {newEmail && newEmail.toLowerCase() !== data?.myProfile?.email_address?.toLowerCase() && !emailVerified && (
                                        <Button
                                            type="button"
                                            onClick={handleSendCode}
                                            disabled={sendingCode || codeSent}
                                            className="h-14 px-6 rounded-lg bg-[#85C349] capitalize text-base font-medium whitespace-nowrap hover:bg-[#75b339] disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {sendingCode ? (
                                                <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                                            ) : codeSent ? (
                                                'Code Sent'
                                            ) : (
                                                'Send Code'
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {codeSent && !emailVerified && (
                                <div className="flex flex-col gap-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <label className="leading-[19.36px] text-[#202020] font-medium">
                                        Verification code
                                    </label>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Enter the 5-digit code sent to {newEmail}
                                    </p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={verificationCode}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 5)
                                                setVerificationCode(value)
                                            }}
                                            placeholder="Enter 5-digit code"
                                            maxLength={5}
                                            className="flex-1 h-14 rounded-lg border border-[#CBD7DD] px-4 placeholder:text-base focus:border-primary focus:ring-primary text-center text-2xl tracking-widest font-mono"
                                        />
                                        <Button
                                            type="button"
                                            onClick={handleVerifyCode}
                                            disabled={verifyingCode || verificationCode.length !== 5}
                                            className="h-14 px-6 rounded-lg bg-[#85C349] capitalize text-base font-medium whitespace-nowrap hover:bg-[#75b339] disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {verifyingCode ? (
                                                <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                                            ) : (
                                                'Verify'
                                            )}
                                        </Button>
                                    </div>
                                    <div className="flex justify-between items-center mt-2">
                                        <button
                                            type="button"
                                            onClick={handleResendCode}
                                            disabled={resendCooldown > 0}
                                            className="text-sm text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                                        >
                                            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCodeSent(false)
                                                setVerificationCode('')
                                                setNewEmail('')
                                            }}
                                            className="text-sm text-gray-600 hover:underline"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-col gap-2">
                                <label>Phone Number (For Text updates)</label>
                                <div className="flex h-14 w-full overflow-hidden rounded-lg border border-[#CBD7DD] focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
                                    <span className="flex items-center border-r border-[#CBD7DD] bg-gray-50 px-3 text-sm text-gray-500 select-none">
                                        🇺🇸 +1
                                    </span>
                                    <input
                                        type="text"
                                        id="phoneNumber"
                                        value={(() => {
                                            const d = formik.values.phoneNumber || ''
                                            if (!d.length) return ''
                                            if (d.length <= 3) return `(${d}`
                                            if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
                                            return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`
                                        })()}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))
                                        }}
                                        placeholder="(555) 555-5555"
                                        className="h-full flex-1 px-4 placeholder:text-base focus:outline-none"
                                    />
                                </div>
                                <ErrorMessage
                                    name="phoneNumber"
                                    component="div"
                                    className="text-sm text-red-500"
                                />
                            </div>
                            {data?.myProfile?.user_type?.toLowerCase() !== 'general' && (
                                 <div className="flex flex-col gap-2">
                                    <label className="leading-[19.36px] text-[#202020]">
                                        Street Address
                                    </label>
                                    <Field
                                        type="text"
                                        name="streetAddress"
                                        placeholder="Enter address"
                                        className="h-14 w-full rounded-lg border border-[#CBD7DD]  px-4 placeholder:text-base focus:border-primary focus:ring-primary"
                                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleStreetAddressBlur(e.target.value, formik.values.city, formik.values.state, setFieldValue)}
                                    />
                                    <ErrorMessage
                                        name="streetAddress"
                                        component="div"
                                        className="text-sm text-red-500"
                                    />
                                    {isProducer && (
                                        <p className="text-xs text-gray-500">Add your selling locations on the locations tab. This address is for internal use only and not visible to the public.</p>
                                    )}
                                </div>
                            )}

                            <div>
                                <p className='text-gray-600 border-b mb-3'>Add your location</p>
                                <div className="flex sm:flex-row flex-col gap-6">
                                    <div className="flex flex-1 flex-col gap-2">
                                        <label className="leading-[19.36px] text-[#202020]">
                                            State
                                        </label>
                                        <Field
                                            as="select"
                                            name="state"
                                            component={(props: any) => (
                                                <Select
                                                {...props}
                                                options={statesncities.map((state) => {
                                                    return {
                                                        value: state.abbr,
                                                        label: state.state
                                                    }
                                                })}
                                                value={statesncities.map((state) => {
                                                    return {
                                                        value: state.abbr,
                                                        label: state.state
                                                    }
                                                }).find((option) => option.value === props.field.value)}
                                                onChange={(e: any) => handleStateChange(e as SingleValue<Option>, setFieldValue)}
                                                styles={{
                                                    control: (provided: any, state: any) => ({
                                                    ...provided,
                                                    height: '56px',
                                                    width: '100%',
                                                    borderRadius: '0.5rem',
                                                    borderColor: state.isFocused ? '#2563eb' : '#CBD7DD',
                                                    padding: '0 0.375em',
                                                    fontSize: '1rem', 
                                                    color: formik.values.state ? '#000000' : '#6b7280',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
                                                    }),
                                                    placeholder: (provided: any) => ({
                                                    ...provided,
                                                    color: '#6b7280',
                                                    }),
                                                }}
                                                />
                                            )}
                                        />
                                        <ErrorMessage
                                            name="state"
                                            component="div"
                                            className="text-sm text-red-500"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col gap-2">
                                        <label className="leading-[19.36px] text-[#202020]">
                                            City
                                        </label>
                                        <Field
                                            as="select"
                                            name="city"
                                            component={(props: any) => {
                                                const cities = formik && formik.values.state && statesncities.find((item) => item.abbr === formik.values.state)?.cities.map((city) => {
                                                return {
                                                    value: city,
                                                    label: city
                                                }
                                                }) || [];
                                                return (
                                                    <Select
                                                        {...props}
                                                        options={cities}
                                                        value={cities.find((option) => option.value === props.field.value)}
                                                        onChange={(e: any) => handleCityChange(e as SingleValue<Option>, setFieldValue, formik.values.streetAddress, formik.values.state)}
                                                        isDisabled={!formik.values.state}
                                                        styles={{
                                                        control: (provided: any, state: any) => ({
                                                            ...provided,
                                                            height: '56px',
                                                            width: '100%',
                                                            borderRadius: '0.5rem',
                                                            borderColor: state.isFocused ? '#2563eb' : '#CBD7DD',
                                                            padding: '0 0.375em',
                                                            fontSize: '1rem',
                                                            color: formik.values.state ? '#000000' : '#6b7280',
                                                            boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
                                                        }),
                                                        placeholder: (provided: any) => ({
                                                            ...provided,
                                                            color: '#6b7280',
                                                        }),
                                                        }}
                                                    />
                                                    );
                                                }}
                                                placeholder="Select option"
                                            />
                                            <ErrorMessage
                                                name="city"
                                                component="div"
                                                className="text-sm text-red-500"
                                            />
                                    </div>
                                    <div className=" flex flex-1 flex-col gap-2">
                                        <label className="leading-[19.36px] text-[#202020]">
                                            Zip
                                        </label>
                                        <Field
                                            type="text"
                                            name="zip"
                                            placeholder="Zip code"
                                            className="h-14 w-full rounded-lg border border-[#CBD7DD]  px-4 placeholder:text-base focus:border-primary focus:ring-primary"
                                        />
                                        <ErrorMessage
                                            name="zip"
                                            component="div"
                                            className="text-sm text-red-500"
                                        />
                                    </div>
                                </div>

                                {!isBusiness && (
                                    <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3">
                                        <p className="mb-2 text-xs text-gray-500">
                                            Your city and state are only used internally to enhance and localize your experience.
                                        </p>
                                        <label className="flex cursor-pointer items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={locationOptedOut}
                                                onChange={(e) => handleLocationOptOut(e.target.checked)}
                                                className="h-3.5 w-3.5 rounded border-gray-300 accent-[#85C349]"
                                            />
                                            <span className="text-xs text-gray-500">I prefer not to share my location</span>
                                        </label>
                                    </div>
                                )}
                            </div>

                            {isMarket && (
                                <div className="flex flex-col gap-3">
                                    <p className="text-gray-600 border-b pb-1">Hours of Operation</p>
                                    <FieldArray name="operatingHours">
                                        {({ remove, push }) => (
                                            <div className="flex flex-col gap-4">
                                                {formik.values.operatingHours.map((hour: any, index: number) => (
                                                    <div key={hour.id ?? index} className="flex items-center gap-3">
                                                        <div className="flex-shrink-0 w-36">
                                                            <Field
                                                                as="select"
                                                                name={`operatingHours[${index}].day`}
                                                                className="h-14 w-full rounded-lg border border-[#CBD7DD] px-2 focus:border-primary focus:ring-primary"
                                                            >
                                                                <option value="">Day</option>
                                                                {[
                                                                    { name: 'Monday', abbr: 'Mon' },
                                                                    { name: 'Tuesday', abbr: 'Tue' },
                                                                    { name: 'Wednesday', abbr: 'Wed' },
                                                                    { name: 'Thursday', abbr: 'Thu' },
                                                                    { name: 'Friday', abbr: 'Fri' },
                                                                    { name: 'Saturday', abbr: 'Sat' },
                                                                    { name: 'Sunday', abbr: 'Sun' },
                                                                ].map((d) => (
                                                                    <option key={d.abbr} value={d.abbr}>{d.name}</option>
                                                                ))}
                                                            </Field>
                                                        </div>
                                                        <div className="flex-1">
                                                            <Field
                                                                type="text"
                                                                name={`operatingHours[${index}].time`}
                                                                placeholder="e.g. 8:00am - 12:00pm"
                                                                className="h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-sm focus:border-primary focus:ring-primary"
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(index)}
                                                            className="group h-10 w-10 flex-shrink-0 rounded-md bg-[#16270C1A] p-2 hover:bg-[#FF3B301A]"
                                                        >
                                                            <img src="/images/trash.svg" alt="remove" className="h-full w-full opacity-40 grayscale group-hover:opacity-100 group-hover:grayscale-0" />
                                                        </button>
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => push({ id: Date.now(), day: '', time: '' })}
                                                    className="flex items-center gap-2 self-start rounded-md bg-[#F3FAEB] px-4 py-2.5 text-sm font-medium text-[#548828]"
                                                >
                                                    <img src="/images/plusGreen.svg" alt="add" className="h-5 w-5" />
                                                    Add Day
                                                </button>
                                            </div>
                                        )}
                                    </FieldArray>
                                </div>
                            )}

                            {isBusiness && (
                                <div className="flex flex-col gap-3">
                                    <p className="text-gray-600 border-b pb-1">Online Presence</p>
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm text-[#202020]">Website</label>
                                        <Field
                                            type="url"
                                            name="website"
                                            placeholder="https://yourwebsite.com"
                                            className="h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-sm focus:border-primary focus:ring-primary"
                                        />
                                        <ErrorMessage name="website" component="div" className="text-sm text-red-500" />
                                    </div>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-[#202020]">Facebook</label>
                                            <Field
                                                type="url"
                                                name="facebook"
                                                placeholder="https://facebook.com/yourpage"
                                                className="h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-sm focus:border-primary focus:ring-primary"
                                            />
                                            <ErrorMessage name="facebook" component="div" className="text-sm text-red-500" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-[#202020]">Instagram</label>
                                            <Field
                                                type="url"
                                                name="instagram"
                                                placeholder="https://instagram.com/yourhandle"
                                                className="h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-sm focus:border-primary focus:ring-primary"
                                            />
                                            <ErrorMessage name="instagram" component="div" className="text-sm text-red-500" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-[#202020]">X / Twitter</label>
                                            <Field
                                                type="url"
                                                name="twitter"
                                                placeholder="https://twitter.com/yourhandle"
                                                className="h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-sm focus:border-primary focus:ring-primary"
                                            />
                                            <ErrorMessage name="twitter" component="div" className="text-sm text-red-500" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isProducer && (
                                <div className="flex flex-col gap-3">
                                    <p className="text-gray-600 border-b pb-1">Certifications</p>
                                    <CatalogPicker
                                        compact
                                        searchPlaceholder="Search Certificate..."
                                        sidebarTitle="Certificates"
                                        sidebarHint="Add the certificates you won or received."
                                        items={certItems}
                                        selected={selectedCertItems}
                                        loading={practicesLoading}
                                        onSearch={setCertSearchTerm}
                                        onToggle={toggleCert}
                                        onClear={() => setSelectedPractices([])}
                                    />
                                </div>
                            )}

                            <div className="mt-6 flex justify-end gap-4">
                                <button type="button" onClick={handleProfileSettingsOpen} className='px-4 bg-transparent text-gray-600 hover:bg-gray-100 py-2 rounded-full text-base font-medium'>
                                    Cancel
                                </button>
                                <Button
                                    className="w-28 rounded-full bg-[#85C349] capitalize text-base font-medium"
                                    type="button"
                                    disabled={isSubmitting}
                                    onClick={async () => {
                                        const errors = await formik.validateForm()
                                        if (Object.keys(errors).length > 0) {
                                            formik.setTouched(
                                                Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<string, boolean>)
                                            )
                                            const fieldNames = Object.keys(errors).map((key) => FIELD_LABELS[key] || key).join(', ')
                                            toast.error(`Please fix before saving: ${fieldNames}`)
                                            return
                                        }
                                        formik.submitForm()
                                    }}
                                    >
                                    {isSubmitting ? (
                                        <FontAwesomeIcon
                                        icon={faSpinner}
                                        className={' h-6 w-6 animate-spin text-white'}
                                        />
                                    ) : (
                                        'Save'
                                    )}
                                </Button>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </div>
    )
}

export default AccountInfo
